import Button from "@/components/common/button/Button";
import Input from "@/components/common/input/Input";
import Modal from "@/components/common/modal/Modal";
import DataSelect from "@/components/common/select/DataSelect";
import AlertMessage from "@/components/layout/message/AlertMessage";
import { Eye, EyeOff } from "lucide-react";
import React, { useEffect } from "react";
import styles from "../FormModal.module.css";
import type { userFormModalProps } from "./types.props";
import { useUserForm } from "./useUserForm";

export const ROLE_OPTIONS = [
    { value: "Estudiante", label: "Estudiante" },
    { value: "Coordinador de curso", label: "Coordinador de curso" },
    { value: "Facilitador", label: "Facilitador" },
];

export const STATUS_OPTIONS = [
    { value: "active", label: "Activo" },
    { value: "inactive", label: "Inactivo" },
];

const UserFormModal: React.FC<userFormModalProps> = ({
    isOpen,
    isLoading,
    initialData,
    apiError,
    institutions,
    onClose,
    onSubmit,
    onInstitutionSearchChange,
}) => {
    const {
        formData,
        formErrors,
        setFormErrors,
        isEditMode,
        showPassword,
        handleInputChange,
        validateForm,
        togglePasswordVisibility,
    } = useUserForm(initialData);

    useEffect(() => {
        if (apiError) {
            setFormErrors(apiError);
        }
    }, [apiError, setFormErrors]);

    const handleInstitutionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleInputChange(e);
        if (onInstitutionSearchChange) {
            onInstitutionSearchChange(e.target.value);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (validateForm()) {
            onSubmit(formData);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <form onSubmit={handleSubmit} className={styles["form"]}>
                <h2 className={styles["form-title"]}>
                    {isEditMode ? "Modificar usuario" : "Añadir usuario"}
                </h2>

                <Input
                    id="identifier"
                    name="identifier"
                    label="Identificador (único)"
                    value={formData.identifier || ""}
                    onChange={handleInputChange}
                    error={formErrors?.fieldErrors?.identifier}
                    required
                />

                <div className={styles["form-grid"]}>
                    <Input
                        id="first_name"
                        name="first_name"
                        label="Primer nombre"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        error={formErrors?.fieldErrors?.first_name}
                        required
                    />
                    <Input
                        id="second_name"
                        name="second_name"
                        label="Segundo nombre"
                        value={formData.second_name}
                        onChange={handleInputChange}
                        error={formErrors?.fieldErrors?.second_name}
                    />
                </div>
                <div className={styles["form-grid"]}>
                    <Input
                        id="last_name"
                        name="last_name"
                        label="Primer apellido"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        error={formErrors?.fieldErrors?.last_name}
                        required
                    />
                    <Input
                        id="second_last_name"
                        name="second_last_name"
                        label="Segundo apellido"
                        value={formData.second_last_name}
                        onChange={handleInputChange}
                        error={formErrors?.fieldErrors?.second_last_name}
                    />
                </div>

                <Input
                    id="email"
                    type="email"
                    name="email"
                    label="Correo Electrónico"
                    value={formData.email}
                    onChange={handleInputChange}
                    error={formErrors?.fieldErrors?.email}
                    required
                />

                <div className={styles["password-input-wrapper"]}>
                    <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        name="password"
                        label="Contraseña"
                        value={formData.password}
                        onChange={handleInputChange}
                        error={formErrors?.fieldErrors?.password}
                        placeholder={isEditMode ? "Dejar en blanco para no cambiar" : ""}
                        required={!isEditMode}
                    />
                    <button
                        type="button"
                        className={styles["password-toggle"]}
                        onClick={togglePasswordVisibility}
                        aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                </div>

                <div className={styles["form-grid"]}>
                    <DataSelect
                        name="role"
                        label="Rol"
                        value={formData.role}
                        onChange={handleInputChange}
                        options={ROLE_OPTIONS}
                        error={formErrors?.fieldErrors?.role}
                        required
                    />
                    <DataSelect
                        name="status"
                        label="Estado"
                        value={formData.status}
                        onChange={handleInputChange}
                        options={STATUS_OPTIONS}
                        error={formErrors?.fieldErrors?.status}
                        required
                    />
                </div>

                <Input
                    id="institution"
                    name="institution"
                    label="Institución"
                    value={formData.institution?.name}
                    onChange={handleInstitutionChange}
                    error={formErrors?.fieldErrors?.institution}
                    list="institutions-list"
                    disabled={formData.role === "Facilitador"}
                    required={formData.role !== "Facilitador"}
                />

                <datalist id="institutions-list">
                    {institutions &&
                        institutions.map((inst) => (
                            <option key={inst.id} value={inst.name} />
                        ))}
                </datalist>

                <AlertMessage type="error" title="Error en el formulario">
                    {formErrors?.generalMessage}
                </AlertMessage>
                <div className={styles["form-actions"]}>
                    <Button type="button" variant="secondary" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button type="submit" variant="primary" isLoading={isLoading}>
                        {isEditMode ? "Actualizar" : "Crear usuario"}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default UserFormModal;
